import { isNil } from 'Utility/ramdaReplacement';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import FormRow from 'Components/FormRow';
import { Container, Item } from 'Components/Grid';
import InputHandler from 'Components/InputHandler';
import { ReferenceText } from '../../../../Diagnosis/utils/styled';
import { relapseScore, t1EnchancedChange, t2Change } from '../../../utils';
import { isPartialDate, nowPartialDate } from 'neuro-utils';
import { getMRIChanges, getRelapseCount } from 'Routes/InflammatoryDiseaseActivity/utils/idaClassification2024';
import { inflammatoryDiseaseActivityStart } from 'neuro-schemas';

const { idaClassificationStart2024, idaClassificationStart2020 } = inflammatoryDiseaseActivityStart.utils;

const optFormatter = (name: string | number): React.JSX.Element => (
  <FormattedMessage id={'inflammatoryDiseaseActivity.opts.' + name} />
);

const relapseCountLast12MonthsOnChange = (
  version: '2020' | '2024',
  onChange: IOwnProps['formData']['onChange'],
  relapseDocs: IDocumentWithTypeV2<'relapse'>[],
  date?: PartialDate,
): void =>
  onChange &&
  onChange({
    relapseCountLast12Months: {
      2020: relapseScore(relapseDocs, date),
      2024: getRelapseCount(date ?? nowPartialDate(), relapseDocs, 'start'),
    }[version],
  });

const relapseSymptomsRemainOnChange = (onChange: IOwnProps['formData']['onChange']): void =>
  onChange &&
  onChange({
    relapseSymptomsRemain: 'unknown',
  });

const t1GadoliniumEnhancedActiveChanges = (
  version: '2020' | '2024',
  onChange: IOwnProps['formData']['onChange'],
  mriDocs?: IDocumentWithTypeV2<'mri'>[],
  date?: PartialDate,
): void =>
  onChange &&
  onChange({
    T1GadoliniumEnhancedActiveChanges: {
      2020: t1EnchancedChange(mriDocs, date),
      2024: getMRIChanges(date ?? nowPartialDate(), mriDocs ?? [], 'T1GadoliniumEnhanced', 'start'),
    }[version],
  });

const t2ChangesOnChange = (
  version: '2020' | '2024',
  onChange: IOwnProps['formData']['onChange'],
  mriDocs?: IDocumentWithTypeV2<'mri'>[],
  date?: PartialDate,
): void =>
  onChange &&
  onChange({
    T2Changes: {
      2020: t2Change('start', mriDocs, date),
      2024: getMRIChanges(date ?? nowPartialDate(), mriDocs ?? [], 'T2', 'start'),
    }[version],
  });

const t2ChangesNewOnChange = (
  onChange: IOwnProps['formData']['onChange'],
  mriDocs?: IDocumentWithTypeV2<'mri'>[],
  date?: PartialDate,
): void =>
  onChange &&
  onChange({
    T2ChangesNew: getMRIChanges(date ?? nowPartialDate(), mriDocs ?? [], 'T2New', 'start'),
  });

const inflammatoryDiseaseActivity2020OnChange = (
  onChange: IOwnProps['formData']['onChange'],
  document: IInflammatoryDiseaseActivityStart2020,
): void =>
  onChange &&
  onChange({
    inflammatoryDiseaseActivity: idaClassificationStart2020(document),
  });

const inflammatoryDiseaseActivity2024OnChange = (
  onChange: IOwnProps['formData']['onChange'],
  document: IInflammatoryDiseaseActivityStart2024,
): void =>
  onChange &&
  onChange({
    inflammatoryDiseaseActivity: idaClassificationStart2024(document),
  });

const StartForm = ({ formData, viewing, doc, relapseDocs, mriDocs }: IOwnProps): React.JSX.Element | null => {
  const { onChange, document } = formData;
  const idaClassificationChange = React.useRef(!document.inflammatoryDiseaseActivity); // Dont allow IDA to be changed if old value exists when mounting
  const date = document.date;
  const relapse = document.relapseCountLast12Months;
  const t2 = document.T2Changes;
  const t1 = document.T1GadoliniumEnhancedActiveChanges;

  const isNewDoc = !viewing && doc._commitsLength === 1;

  // "MS-tauti. Käypä hoito -suositus. (Julkaistu: 6.2.2024)."
  const versionIs2024 = (d: typeof document): d is IInflammatoryDiseaseActivityStart2024 => {
    return isNewDoc || d.version === '2024';
  };

  // "MS-tauti: Käypä hoito -suositus (Julkaistu: 23.01.2020)."
  const versionIs2020 = (d: typeof document): d is IInflammatoryDiseaseActivityStart2020 => {
    return !isNewDoc && (!d.version || d.version === '2020');
  };

  const version = versionIs2020(document) ? '2020' : '2024';

  React.useEffect(() => {
    if (!viewing && isNewDoc && !document.version && version === '2024') {
      onChange?.({ version: '2024' });
    }
  }, [version]);

  const relapseSymptomsRemain = versionIs2024(document) ? document.relapseSymptomsRemain : undefined;
  const t2New = versionIs2024(document) ? document.T2ChangesNew : undefined;

  // For useEffect dependency comparisons
  const relapseDocsString = React.useMemo(() => JSON.stringify(relapseDocs), [relapseDocs]);
  const mriDocsString = React.useMemo(() => JSON.stringify(mriDocs), [mriDocs]);

  // Call onChange funtions only when certain values are not defined or has changed between renders
  // relapseCountLast12Months
  React.useEffect(() => {
    if (!viewing && isNil(relapse)) relapseCountLast12MonthsOnChange(version, onChange, relapseDocs, date ?? undefined);
  }, [date, relapse, relapseDocsString]);

  // relapseSymptomsRemain
  React.useEffect(() => {
    if (!viewing && version === '2024' && isNil(relapseSymptomsRemain) && relapse === '1')
      relapseSymptomsRemainOnChange(onChange);
  }, [date, relapse, relapseDocsString]);

  // T2Changes
  React.useEffect(() => {
    if (!viewing && isNil(t2)) t2ChangesOnChange(version, onChange, mriDocs, date ?? undefined);
  }, [date, t2, mriDocsString]);

  // T2ChangesNew
  React.useEffect(() => {
    if (!viewing && version === '2024' && isNil(t2New)) t2ChangesNewOnChange(onChange, mriDocs, date ?? undefined);
  }, [date, t2New, mriDocsString]);

  // T1GadoliniumEnhancedActiveChanges
  React.useEffect(() => {
    if (!viewing && isNil(t1)) t1GadoliniumEnhancedActiveChanges(version, onChange, mriDocs, date ?? undefined);
  }, [date, t1, mriDocsString]);

  // Update IDA whenever these 3 values change, if the IDA value doesn't match the calculated value
  React.useEffect(() => {
    if (!viewing && idaClassificationChange.current) {
      if (versionIs2024(document) && document.inflammatoryDiseaseActivity !== idaClassificationStart2024(document)) {
        inflammatoryDiseaseActivity2024OnChange(onChange, document);
      }
      if (versionIs2020(document) && document.inflammatoryDiseaseActivity !== idaClassificationStart2020(document)) {
        inflammatoryDiseaseActivity2020OnChange(onChange, document);
      }
    }
  }, [relapse, relapseSymptomsRemain, t2, t2New, t1]);

  const handleChange = (values: TOnChangeValues): void => {
    onChange && onChange(values);
    idaClassificationChange.current = true; // Allow IDA to be changed if any value is manually changed
  };

  let relapseCountLast12MonthsOptions: string[] | undefined = undefined;
  let T1GadoliniumEnhancedActiveChangesOptions: string[] | undefined = undefined;
  let inflammatoryDiseaseActivityOptions: string[] | undefined = undefined;

  // Pick options for fields that are shared between 2020 and 2024 versions
  if (versionIs2024(document)) {
    relapseCountLast12MonthsOptions = ['0', '1', '≥2'];
    T1GadoliniumEnhancedActiveChangesOptions = ['0', '≥1', 'unknown'];
    inflammatoryDiseaseActivityOptions = ['veryActiveMS', 'noIndicationOfVeryActiveMS', 'unknown'];
  } else if (versionIs2020(document)) {
    relapseCountLast12MonthsOptions = ['0', '1', '≥1'];
    T1GadoliniumEnhancedActiveChangesOptions = ['0', '1', '>1', 'unknown'];
    inflammatoryDiseaseActivityOptions = ['activeMS', 'veryActiveMS', 'unknown'];
  }

  const dateAndRelapseDocsExist = isPartialDate(date) && Array.isArray(relapseDocs);
  const dateAndMriDocsExist = isPartialDate(date) && Array.isArray(mriDocs);

  type PartialRecord<K extends keyof any, T> = Partial<Record<K, T>>;

  const automaticallyCalculatedValues: PartialRecord<keyof typeof document, any> = {};

  // Set automatically calculated values based on form version
  if (versionIs2024(document)) {
    if (dateAndRelapseDocsExist) {
      if (document.relapseCountLast12Months !== getRelapseCount(date, relapseDocs, 'start')) {
        automaticallyCalculatedValues.relapseCountLast12Months = getRelapseCount(date, relapseDocs, 'start');
      }
    }
    if (dateAndMriDocsExist) {
      (['T2', 'T2New', 'T1GadoliniumEnhanced'] as ('T2' | 'T2New' | 'T1GadoliniumEnhanced')[]).forEach(type => {
        const key = (
          type === 'T2'
            ? 'T2Changes'
            : type === 'T1GadoliniumEnhanced'
              ? 'T1GadoliniumEnhancedActiveChanges'
              : 'T2ChangesNew'
        ) as keyof typeof automaticallyCalculatedValues;
        if (document[key] !== getMRIChanges(date, mriDocs, type, 'start')) {
          automaticallyCalculatedValues[key] = getMRIChanges(date, mriDocs, type, 'start');
        }
      });
    }
    if (document.inflammatoryDiseaseActivity !== idaClassificationStart2024(document)) {
      automaticallyCalculatedValues.inflammatoryDiseaseActivity = idaClassificationStart2024(document);
    }
  } else if (versionIs2020(document)) {
    if (document.relapseCountLast12Months !== relapseScore(relapseDocs, date ?? undefined)) {
      automaticallyCalculatedValues.relapseCountLast12Months = relapseScore(relapseDocs, date ?? undefined);
    }
    if (document.T2Changes !== t2Change('start', mriDocs, date ?? undefined)) {
      automaticallyCalculatedValues.T2Changes = t2Change('start', mriDocs, date ?? undefined);
    }
    if (document.T1GadoliniumEnhancedActiveChanges !== t1EnchancedChange(mriDocs, date ?? undefined)) {
      automaticallyCalculatedValues.T1GadoliniumEnhancedActiveChanges = t1EnchancedChange(mriDocs, date ?? undefined);
    }
    if (document.inflammatoryDiseaseActivity !== idaClassificationStart2020(document)) {
      automaticallyCalculatedValues.inflammatoryDiseaseActivity = idaClassificationStart2020(document);
    }
  }

  return (
    <React.Fragment>
      <FormRow title="general.date">
        <InputHandler
          type="PartialDate"
          editing={!viewing}
          name="date"
          formData={{
            onChange: handleChange,
            document: document,
          }}
          dateDefault="now"
          isNotCancellable={true}
        />
      </FormRow>
      <FormRow
        title="inflammatoryDiseaseActivity.relapseCountLast12Months"
        description={
          <FormattedMessage
            id={`inflammatoryDiseaseActivity.relapseCountLast12MonthsInfo${versionIs2024(document) ? 2024 : 2020}`}
          />
        }
      >
        <Container>
          <Item xs={7}>
            <InputHandler
              type="Radio"
              editing={!viewing}
              name="relapseCountLast12Months"
              formData={{
                onChange: handleChange,
                document: document,
              }}
              options={relapseCountLast12MonthsOptions}
              optionFormatter={(id: string | number) =>
                optFormatter(id === '≥1' && versionIs2020(document) ? '≥1RelapseDifficultyLevelConsidered' : id)
              }
            />
          </Item>
          <Item xs={5}>
            {!viewing && automaticallyCalculatedValues.relapseCountLast12Months ? (
              <div>
                <FormattedMessage id="inflammatoryDiseaseActivity.automaticallyCalculatedValue" />
                <span>{': '}</span>
                <span>{optFormatter(automaticallyCalculatedValues.relapseCountLast12Months)}</span>
              </div>
            ) : undefined}
          </Item>
        </Container>
      </FormRow>
      <FormRow
        title="inflammatoryDiseaseActivity.relapseSymptomsRemain"
        description={<FormattedMessage id="inflammatoryDiseaseActivity.relapseSymptomsRemainInfo" />}
        condition={versionIs2024(document) && document.relapseCountLast12Months === '1'}
      >
        <Container>
          <Item xs={7}>
            <InputHandler
              type="Radio"
              editing={!viewing}
              name="relapseSymptomsRemain"
              formData={{
                onChange: handleChange,
                document: document,
              }}
              options={['yes', 'no', 'unknown']}
              optionFormatter={optFormatter}
            />
          </Item>
        </Container>
      </FormRow>
      <FormRow
        title="inflammatoryDiseaseActivity.T2Changes"
        description={
          <FormattedMessage id={`inflammatoryDiseaseActivity.T2ChangesInfo${versionIs2024(document) ? 2024 : 2020}`} />
        }
      >
        <Container>
          <Item xs={7}>
            <InputHandler
              type="Radio"
              editing={!viewing}
              name="T2Changes"
              formData={{
                onChange: handleChange,
                document: document,
              }}
              options={['0', '1-8', '≥9', 'unknown']}
              optionFormatter={optFormatter}
            />
          </Item>
          <Item xs={5}>
            {!viewing && automaticallyCalculatedValues.T2Changes ? (
              <div>
                <FormattedMessage id="inflammatoryDiseaseActivity.automaticallyCalculatedValue" />
                <span>{': '}</span>
                <span>{optFormatter(automaticallyCalculatedValues.T2Changes)}</span>
              </div>
            ) : undefined}
          </Item>
        </Container>
      </FormRow>
      <FormRow
        title="inflammatoryDiseaseActivity.T2ChangesNew"
        description={<FormattedMessage id="inflammatoryDiseaseActivity.T2ChangesNewInfo" />}
        condition={versionIs2024(document)}
      >
        <Container>
          <Item xs={7}>
            <InputHandler
              type="Radio"
              editing={!viewing}
              name="T2ChangesNew"
              formData={{
                onChange: handleChange,
                document: document,
              }}
              options={['0', '≥1', 'unknown']}
              optionFormatter={optFormatter}
            />
          </Item>
          <Item xs={5}>
            {!viewing && automaticallyCalculatedValues['T2ChangesNew' as keyof typeof automaticallyCalculatedValues] ? (
              <div>
                <FormattedMessage id="inflammatoryDiseaseActivity.automaticallyCalculatedValue" />
                <span>{': '}</span>
                <span>
                  {optFormatter(
                    automaticallyCalculatedValues['T2ChangesNew' as keyof typeof automaticallyCalculatedValues],
                  )}
                </span>
              </div>
            ) : undefined}
          </Item>
        </Container>
      </FormRow>
      <FormRow
        title={`inflammatoryDiseaseActivity.T1GadoliniumEnhancedActiveChanges${versionIs2024(document) ? 2024 : 2020}`}
        description={
          <FormattedMessage
            id={`inflammatoryDiseaseActivity.T1GadoliniumEnhancedActiveChangesInfo${
              versionIs2024(document) ? 2024 : 2020
            }`}
          />
        }
      >
        <Container>
          <Item xs={7}>
            <InputHandler
              type="Radio"
              editing={!viewing}
              name="T1GadoliniumEnhancedActiveChanges"
              formData={{
                onChange: handleChange,
                document: document,
              }}
              options={T1GadoliniumEnhancedActiveChangesOptions}
              optionFormatter={optFormatter}
            />
          </Item>
          <Item xs={5}>
            {!viewing && automaticallyCalculatedValues.T1GadoliniumEnhancedActiveChanges ? (
              <div>
                <FormattedMessage id="inflammatoryDiseaseActivity.automaticallyCalculatedValue" />
                <span>{': '}</span>
                <span>{optFormatter(automaticallyCalculatedValues.T1GadoliniumEnhancedActiveChanges)}</span>
              </div>
            ) : undefined}
          </Item>
        </Container>
      </FormRow>
      <FormRow
        title="inflammatoryDiseaseActivity.inflammatoryDiseaseActivity"
        description={<FormattedMessage id="inflammatoryDiseaseActivity.inflammatoryDiseaseActivityInfo" />}
      >
        <Container>
          <Item xs={7}>
            <InputHandler
              type="Radio"
              editing={!viewing}
              name="inflammatoryDiseaseActivity"
              formData={formData}
              options={inflammatoryDiseaseActivityOptions}
              optionFormatter={optFormatter}
            />
          </Item>
          <Item xs={5}>
            {!viewing && automaticallyCalculatedValues.inflammatoryDiseaseActivity ? (
              <div>
                <FormattedMessage id="inflammatoryDiseaseActivity.automaticallyCalculatedValue" />
                <span>{': '}</span>
                <span>{optFormatter(automaticallyCalculatedValues.inflammatoryDiseaseActivity)}</span>
              </div>
            ) : undefined}
          </Item>
        </Container>
      </FormRow>
      <ReferenceText>
        <FormattedMessage id={`inflammatoryDiseaseActivity.referenceText${versionIs2024(document) ? 2024 : 2020}`} />
      </ReferenceText>
    </React.Fragment>
  );
};

interface IOwnProps {
  formData: IFormData<IDocumentWithTypeV2<'inflammatoryDiseaseActivityStart'>>;
  viewing?: string;
  doc: IDocumentWithTypeV2<'inflammatoryDiseaseActivityStart'>;
  relapseDocs: IDocumentWithTypeV2<'relapse'>[];
  mriDocs: IDocumentWithTypeV2<'mri'>[];
}

export default StartForm;
